import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';

const WEEKDAYS = ['LU', 'MA', 'MI', 'JU', 'VI', 'SÁ', 'DO'];

const startOfDay = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const isSameDay = (a, b) =>
  !!a && !!b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

// Semana empieza en lunes: getDay() domingo=0 -> lo movemos al final
const buildMonthGrid = (year, month) => {
  const firstOfMonth = new Date(year, month, 1);
  const startOffset = (firstOfMonth.getDay() + 6) % 7; // lunes=0
  const gridStart = new Date(year, month, 1 - startOffset);

  return Array.from({ length: 42 }, (_, i) => {
    const date = new Date(gridStart);
    date.setDate(gridStart.getDate() + i);
    return date;
  });
};

const DatePickerPopover = ({ value, onChange, placeholder = 'Seleccionar fecha' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [viewDate, setViewDate] = useState(value ? new Date(value) : new Date());
  const containerRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setViewDate(value ? new Date(value) : new Date());
    }
  }, [isOpen, value]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const today = useMemo(() => startOfDay(new Date()), []);
  const monthGrid = useMemo(
    () => buildMonthGrid(viewDate.getFullYear(), viewDate.getMonth()),
    [viewDate]
  );

  const monthLabel = viewDate.toLocaleDateString('es-AR', { month: 'long', year: 'numeric' });

  const goToMonth = (delta) => {
    setViewDate((prev) => new Date(prev.getFullYear(), prev.getMonth() + delta, 1));
  };

  const handleSelectDay = (day) => {
    onChange(startOfDay(day));
    setIsOpen(false);
  };

  const formattedValue = value
    ? new Date(value).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : null;

  return (
    <div ref={containerRef} className="relative inline-block">
      <button
        type="button"
        onClick={() => setIsOpen((o) => !o)}
        className="group inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-900 shadow-sm transition-all hover:border-teal-300 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-teal-500/40"
      >
        <CalendarDays className="h-4 w-4 text-teal-600" />
        <span className={formattedValue ? 'text-gray-900' : 'text-gray-400'}>
          {formattedValue || placeholder}
        </span>
      </button>

      {isOpen && (
        <div
          className="absolute left-0 top-[calc(100%+8px)] z-50 w-72 origin-top-left rounded-2xl border border-gray-100 bg-white p-4 shadow-[0_12px_40px_-8px_rgba(15,23,42,0.18)] animate-[datepicker-pop_120ms_ease-out]"
        >
          <div className="mb-3 flex items-center justify-between">
            <button
              type="button"
              onClick={() => goToMonth(-1)}
              className="flex h-7 w-7 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
              aria-label="Mes anterior"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm font-semibold capitalize text-gray-900">{monthLabel}</span>
            <button
              type="button"
              onClick={() => goToMonth(1)}
              className="flex h-7 w-7 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
              aria-label="Mes siguiente"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          <div className="mb-1 grid grid-cols-7 text-center">
            {WEEKDAYS.map((day) => (
              <span key={day} className="text-[11px] font-semibold tracking-wide text-gray-400">
                {day}
              </span>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-y-1">
            {monthGrid.map((day) => {
              const inCurrentMonth = day.getMonth() === viewDate.getMonth();
              const selected = isSameDay(day, value ? new Date(value) : null);
              const isToday = isSameDay(day, today);

              return (
                <div key={day.toISOString()} className="flex items-center justify-center py-0.5">
                  <button
                    type="button"
                    onClick={() => handleSelectDay(day)}
                    className={[
                      'flex h-8 w-8 items-center justify-center rounded-full text-sm transition-colors',
                      selected
                        ? 'bg-teal-600 font-semibold text-white shadow-sm hover:bg-teal-700'
                        : inCurrentMonth
                        ? 'text-gray-700 hover:bg-teal-50 hover:text-teal-700'
                        : 'text-gray-300 hover:bg-gray-50',
                      isToday && !selected ? 'ring-1 ring-inset ring-teal-400' : '',
                    ].join(' ')}
                  >
                    {day.getDate()}
                  </button>
                </div>
              );
            })}
          </div>

          <div className="mt-3 flex items-center justify-between border-t border-gray-100 pt-3">
            <button
              type="button"
              onClick={() => {
                onChange(null);
                setIsOpen(false);
              }}
              className="text-xs font-medium text-gray-400 transition-colors hover:text-red-500"
            >
              Quitar fecha
            </button>
            <button
              type="button"
              onClick={() => handleSelectDay(today)}
              className="text-xs font-semibold text-teal-600 transition-colors hover:text-teal-700"
            >
              Hoy
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DatePickerPopover;
